import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EducationAuditComment } from 'services/professional/step/step.interface';
import { ActivatedRoute, Router } from '@angular/router';
import {
  STEP_COLOR_STATUSES,
  STEP_TYPES,
  STEP_TYPE_STATUSES,
  STEP_AUDIT_STATUSES,
  STEP_TYPES_NAMES,
} from 'constants/status.constants';
import { COMPLIANCE_CRITERIA } from 'constants/log-step.constants';
import { ProgressHours, StepInstance, UnitsData } from 'services/professional/certificates/certificates.interface';
import { ComputedFrom } from '@certemy/common';
import { ROLE_CODES, ROLE_NAMES } from 'constants/role.constants';
import { TYPES } from 'components/status-label/status-label.constants';
import { isPendingVerificationStatus } from 'utils/step.helpers';
import {
  CONTINUOUS_EDUCATION_UNIT,
  CONTINUOUS_EDUCATION_UNIT_NAME,
} from 'components/organization-certification/step/education/education-step.constants';
import { EDUCATION_TYPES } from 'constants/education-step.constants';
import { FEATURES, FeatureToggleService } from 'features/feature-toggle';
import {
  getCapitalizedEducationUnitName,
  getUnitsData,
} from 'features/professional-step/education/education-step.helpers';
import _get from 'lodash-es/get';
import { REJECT_HINT_TYPES } from 'components/hint/hint.constants';
import { constants, VERIFICATION_TYPE } from 'constants/constants';
import { LOG_STEP_RESPONSIBLE_TO_COMPLETE } from 'constants/log-step.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-step-item',
  templateUrl: './step-item.component.html',
  styleUrls: ['./step-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepItemComponent implements OnInit, OnDestroy {
  @Input() step;
  @Input() toBeAudited = false;
  @Input() organizationContactData = { name: '', email: '' };
  @Input() certificateNote = '';
  @Input() initModalStyle = false;
  @Output() stepClick: EventEmitter<StepInstance> = new EventEmitter();
  readonly COMPLIANCE_CRITERIA = COMPLIANCE_CRITERIA;
  readonly STEP_TYPES = STEP_TYPES;
  readonly STEP_TYPES_NAMES = STEP_TYPES_NAMES;
  readonly STEP_TYPE_STATUSES = STEP_TYPE_STATUSES;
  readonly TYPES = TYPES;
  readonly EXAM_STATUSES = constants.SCORE_EXAM_STATUSES_NAMES;
  readonly LOG_STEP_RESPONSIBLE_TO_COMPLETE = LOG_STEP_RESPONSIBLE_TO_COMPLETE;
  workflowTypeId: number;

  stepWithoutStatus = [STEP_TYPES.PRACTICE_TEST, STEP_TYPES.SCHEDULING, STEP_TYPES.TEST_TAKING];

  @ComputedFrom('step')
  get stepTypeId() {
    if (this.step.step_type_id === undefined) {
      return this.step.typeId;
    }
    return this.step.step_type_id;
  }

  @ComputedFrom('step')
  get statusColorId() {
    if (this.step.status_color === undefined) {
      return this.step.statusColorId;
    }
    return this.step.status_color;
  }

  @ComputedFrom('step')
  get stepId() {
    if (this.step.step_instance_id === undefined) {
      return this.step.id;
    }
    return this.step.step_instance_id;
  }

  @ComputedFrom('step')
  get stepInteractive() {
    if (this.step.is_interactive === undefined) {
      return this.step.isInteractive;
    }
    return this.step.is_interactive;
  }

  @ComputedFrom('step')
  get stepEndDate() {
    if (this.step.end_date === undefined) {
      return this.step.endDate;
    }
    return this.step.end_date;
  }

  @ComputedFrom('step')
  get stepStatusId() {
    if (this.step.status_id === undefined) {
      return this.step.complianceStatus;
    }
    return this.step.status_id;
  }

  @ComputedFrom('step')
  get isRejected() {
    return _get(this.step, 'rejectStepData.isRejected', false);
  }

  @ComputedFrom('step')
  get stepTooltip() {
    let stepTooltip = this.stepTypeId === STEP_TYPES.REGISTRATION_FORM &&
      !this.stepInteractive &&
      this.step.hasIncompletedDemographicStep
      ? 'Step will be available once Demographic step is completed'
      : '';
    if (!stepTooltip && this.step?.tooltipText) {
      stepTooltip = this.step.tooltipText;
    }
    return stepTooltip;
  }

  @ComputedFrom('step')
  get stepRole() {
    return _get(this.step, 'role') || _get(this.step, 'content.role');
  }

  educationUnitName = CONTINUOUS_EDUCATION_UNIT_NAME[CONTINUOUS_EDUCATION_UNIT.HOURS];
  educationAdvancedOptionsAllowed = false;

  @ComputedFrom('step')
  get isEducationStep(): boolean {
    return this.stepTypeId === STEP_TYPES.EDUCATION;
  }

  @ComputedFrom('educationUnitName', 'isEducationStep')
  get resetStepTooltip(): string {
    return this.isEducationStep ? `Review Step/Reset ${this.educationUnitName}` : 'Review/Reset Step';
  }

  @ComputedFrom('isEducationStep')
  get unitsData(): UnitsData {
    return this.isEducationStep ? getUnitsData(this.step.content as ProgressHours) : null;
  }

  @ComputedFrom('step', 'isEducationStep')
  get auditStatusId(): number {
    return this.isEducationStep ? _get(this.step.content, 'auditStatusId') : null;
  }

  @ComputedFrom('step', 'auditStatusId')
  get stepAuditComments(): EducationAuditComment[] {
    return this.auditStatusId ? _get(this.step.content, 'auditComments') : [];
  }

  @ComputedFrom('stepAuditComments', 'certificateNote', 'isAuditPausedStatus', 'isAuditCorrectionStatus')
  get rejectComments(): string[] {
    if (this.isAuditCorrectionStatus) {
      return this.stepAuditComments.map((item) => `${item.activityName}:<br/> ${item.text}`);
    }

    if (this.isAuditPausedStatus) {
      return [this.certificateNote];
    }
    // step in TW and not log new step
    if (this.step.rejectionNote && this.step.typeId !== STEP_TYPES.LOG_NEW) {
      return [this.step.rejectionNote];
    }

    return null;
  }

  @ComputedFrom('stepAuditComments', 'certificateNote', 'isAuditPausedStatus')
  get statusTooltip(): string {
    if (this.isAuditPausedStatus) {
      return this.certificateNote;
    }

    return null;
  }

  @ComputedFrom('auditStatusId')
  get isAuditPausedStatus(): boolean {
    return this.auditStatusId === STEP_AUDIT_STATUSES.CREDENTIAL_PAUSED;
  }

  @ComputedFrom('auditStatusId')
  get isAuditCorrectionStatus(): boolean {
    return this.auditStatusId === STEP_AUDIT_STATUSES.CORRECTIONS_REQUESTED;
  }

  @ComputedFrom('step', 'isEducationStep', 'toBeAudited', 'auditStatusId')
  get isStepForAudit(): boolean {
    const isGeneralCeType =
      this.isEducationStep && EDUCATION_TYPES.GENERAL_CE === _get(this.step, 'content.details.ce_type.id');
    const isCompleted = STEP_TYPE_STATUSES.COMPLETED === this.stepStatusId;
    return !this.auditStatusId && isCompleted && isGeneralCeType && this.toBeAudited;
  }

  @ComputedFrom('step', 'isAuditCorrectionStatus', 'isAuditPausedStatus')
  get rejectType(): REJECT_HINT_TYPES {
    if (this.isAuditCorrectionStatus) {
      return REJECT_HINT_TYPES.AUDIT_REJECT;
    }
    if (this.isAuditPausedStatus) {
      return REJECT_HINT_TYPES.AUDIT_PAUSE;
    }
    if (this.isRejected) {
      return REJECT_HINT_TYPES.STEP;
    }
    return null;
  }

  @ComputedFrom('step')
  get optionalType(): REJECT_HINT_TYPES {
    if (this.step.requiredForCompletion === false) {
      return REJECT_HINT_TYPES.OPTIONAL_STEP;
    }
    return null;
  }

  @ComputedFrom('step', 'auditStatusId', 'isEducationStep')
  get isDueDate(): boolean {
    return (
      (!this.auditStatusId && this.statusColorId === STEP_COLOR_STATUSES.RED) ||
      this.statusColorId === STEP_COLOR_STATUSES.ORANGE
    );
  }

  @ComputedFrom('step')
  get isEducationVerificationRequired(): boolean {
    return this.isEducationStep && this.step.verificationType !== VERIFICATION_TYPE.NO_VERIFICATION;
  }

  @ComputedFrom('step')
  get isPendingVerificationStatus(): boolean {
    return isPendingVerificationStatus(this.stepStatusId);
  }

  @ComputedFrom('step')
  get showResetIcon(): boolean {
    if (this.workflowTypeId) {
      return;
    } else if ([STEP_TYPES.LOG, STEP_TYPES.LOG_NEW].includes(this.stepTypeId)) {
      return this.isPendingVerificationStatus && this.stepRole !== ROLE_NAMES[ROLE_CODES.SUPERVISOR];
    } else if (
      ![
        STEP_TYPES.VERIFICATION,
        STEP_TYPES.LICENSE_VERIFICATION,
        STEP_TYPES.LICENSE_RENEWAL,
        STEP_TYPES.ONBOARDING,
      ].includes(this.stepTypeId)
    ) {
      return this.isPendingVerificationStatus;
    }
    return false;
  }

  @ComputedFrom('step', 'isStepForAudit')
  get stepStatusColor() {
    return this.isStepForAudit ? STEP_COLOR_STATUSES.PENDING : this.statusColorId;
  }

  @ComputedFrom('step', 'auditStatusId', 'isStepForAudit')
  get tooltipHidden() {
    const isSpecificColor = [STEP_COLOR_STATUSES.RED, STEP_COLOR_STATUSES.ORANGE].includes(this.statusColorId);
    return (isSpecificColor && this.step.isPassedDueDate) || this.auditStatusId || this.isStepForAudit;
  }

  constructor(
    private featureToggleService: FeatureToggleService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.workflowTypeId = Number(this.activatedRoute.snapshot.params.workflowTypeId);
    this.featureToggleService
      .hasFeatures(FEATURES.EDUCATION_ADVANCED_STEP)
      .pipe(untilDestroyed(this))
      .subscribe((hasFeature) => {
        this.educationAdvancedOptionsAllowed = hasFeature && _get(this.step.content, 'details.advancedOptionsAllowed');
        this.educationUnitName = getCapitalizedEducationUnitName(
          _get(this.step.content, 'details.continuousEducationUnit.id'),
        );
      });
  }

  ngOnDestroy() {}

  onClick(step: StepInstance) {
    if (this.stepInteractive || this.initModalStyle) {
      this.stepClick.emit(step);
    }
  }

  onRejectHintConfirm() {
    if (this.isAuditCorrectionStatus) {
      const stepInstanceId = this.stepId;
      this.router.navigate(['/professional/transcripts'], { queryParams: { stepInstanceId } });
    }
  }

  navigateToURL(url) {
    window.open(url, '_blank');
  }
}
